// PetrolPumpLicenseRegistration.jsx
import React from "react";

export default function PetrolPumpLicenseRegistration() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            Petrol Pump License
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            Petrol Pump License Compliance Support
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks provides compliance support for Petrol Pump
            License approvals as per petroleum and safety regulations, assisting
            applicants through land scrutiny, safety conditions, and dealership
            requirements with Oil Marketing Companies (OMCs).[web:219][web:222]
          </p>
          <p className="text-gray-700">
            The service focuses on ensuring that land, approvals, and
            documentation satisfy petroleum and explosives safety norms as well
            as local body and fire-clearance expectations before commissioning
            the outlet.[web:218][web:221][web:222]
          </p>
        </div>

        {/* Right: Image (square) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/9796/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=1200"
              alt="Petrol pump forecourt representing fuel retail licensing"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* DOCUMENTS & WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for Petrol Pump License
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks helps applicants assemble the land, identity,
              and safety documentation commonly required when applying for a
              petrol pump dealership and related safety clearances.[web:218][web:222]
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Land ownership or lease documents, along with proof that the
                  plot meets minimum area and land-use norms prescribed by the
                  concerned OMC.[web:218][web:222]
                </li>
                <li>
                  No Objection Certificates (NOCs) from local authorities such
                  as municipal bodies or district administration, as well as
                  clearances like fire and environmental approvals where
                  applicable.[web:218][web:220][web:222]
                </li>
                <li>
                  Safety and layout plans / site layout as per PESO and OMC
                  guidelines, indicating tank positions, dispensers, driveways,
                  and safety distances.[web:219][web:221][web:222]
                </li>
                <li>
                  Identity and basic KYC proof of the applicant or entity
                  (PAN, Aadhaar/passport/voter ID) and supporting financial
                  documents if required by the dealership process.[web:220][web:222][web:223]
                </li>
                <li>
                  Business registration or incorporation documents, plus any
                  dealership or LOI documentation issued by the OMC, depending
                  on stage of the process.[web:219][web:222]
                </li>
              </ul>
            </div>
          </div>

          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm’s support is oriented around petroleum and explosives
              safety norms as well as OMC dealership processes, helping
              applicants navigate multi-authority requirements more smoothly.[web:219][web:221][web:222]
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Expertise in Petroleum Licensing
                </h3>
                <p className="text-gray-700 text-sm">
                  Guidance on dealership eligibility, PESO-linked safety
                  expectations, and coordination touchpoints with OMCs and local
                  regulators for petrol pump projects.[web:219][web:221]
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Regulatory Compliance Assurance
                </h3>
                <p className="text-gray-700 text-sm">
                  Focus on aligning land, safety plans, and approvals with
                  applicable petroleum, explosives, and local-body regulations
                  to reduce compliance gaps.[web:218][web:221][web:222]
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Advisory Services
                </h3>
                <p className="text-gray-700 text-sm">
                  Structured, professional assistance from initial evaluation
                  and documentation through submission, clarifications, and
                  final approvals for the petrol pump license.[web:219][web:220]
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
              >
                Get Consultation
              </a>
              <a
                href="tel:+919217468906"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
              >
                Call Hawkins &amp; Brooks
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
